import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n';
import { LogIn, Lock, User, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';

const defaultLocale = () => {
  if (typeof document !== 'undefined' && document.documentElement.lang) return document.documentElement.lang;
  return 'cs';
};

export default function LoginPage() {
  const locale = defaultLocale();
  const t = (key: string) => getTranslation(locale, key);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!username.trim()) {
      setError(t('login.error_username_required'));
      return;
    }
    
    if (!password) {
      setError(t('login.error_password_required'));
      return;
    }
    
    setIsLoading(true);

    try {
      const result = await signIn(username.trim(), password);
      
      if (result.error) {
        setError(result.error);
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || t('login.error_login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetUsername.trim()) {
      setResetMessage(t('login.error_username_required'));
      return;
    }

    setIsResetting(true);
    setResetMessage(null);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: resetUsername.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setResetMessage(data.error || 'Chyba pri obnove hesla');
      } else {
        setResetMessage(data.message || t('login.reset_success'));
        setResetUsername('');
        setTimeout(() => {
          setShowResetForm(false);
          setResetMessage(null);
        }, 5000);
      }
    } catch (err: any) {
      setResetMessage(err.message || t('login.reset_send_error'));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ds-bg dark:bg-slate-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-ds-h1 text-slate-900 dark:text-white mb-2">
            MarryPlan
          </h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400">
            {t('login.subtitle')}
          </p>
        </div>

        <Card>
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-ds-input p-4">
                <p className="text-ds-body text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <Input
              label={t('login.username_label')}
              type="text"
              required
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.username_placeholder')}
              disabled={isLoading}
              icon={<User size={20} />}
            />

            <Input
              label={t('login.password_label')}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.password_placeholder')}
              disabled={isLoading}
              icon={<Lock size={20} />}
            />

            <Button
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
              icon={!isLoading ? <LogIn size={20} /> : undefined}
              className="w-full"
            >
              {isLoading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            {!showResetForm ? (
              <p className="text-ds-meta text-center text-slate-500 dark:text-slate-400">
                {t('login.forgot_password')}{' '}
                <button
                  type="button"
                  onClick={() => setShowResetForm(true)}
                  className="text-brand-600 dark:text-brand-400 hover:underline font-medium"
                >
                  {t('login.reset_password')}
                </button>
              </p>
            ) : (
              <div className="space-y-4">
                <Input
                  label={t('login.username_label')}
                  type="text"
                  value={resetUsername}
                  onChange={(e) => setResetUsername(e.target.value)}
                  placeholder={t('login.username_placeholder')}
                  disabled={isResetting}
                  icon={<User size={20} />}
                />

                {resetMessage && (
                  <div className={`p-3 rounded-ds-input text-ds-body ${
                    resetMessage.includes('error') || resetMessage.includes('Chyba') || resetMessage.includes('Error')
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  }`}>
                    {resetMessage}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={isResetting}
                    isLoading={isResetting}
                    icon={!isResetting ? <Mail size={16} /> : undefined}
                    className="flex-1"
                  >
                    {isResetting ? t('login.reset_sending') : t('login.reset_send_button')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowResetForm(false);
                      setResetUsername('');
                      setResetMessage(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>

                <p className="text-ds-meta text-center text-slate-500 dark:text-slate-400">
                  {t('login.no_email')}{' '}
                  <span className="font-medium">{t('login.contact_admin')}</span>
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
